import { LitElement, css, html, classMap, styleMap } from 'lit-all'

class NovaProductCardQuickShop extends LitElement {
  static properties = {
    productOptions: {
      type: Array,
      attribute: 'product-options',
      converter: (value) => value.split(','),
    },
    variants: { type: Array },
    activeVariant: {
      type: Number,
      attribute: 'active-variant',
    },
    productCardType: {
      type: String,
    },
    swatches: {
      type: Array,
    },
  }

  get optionsToShow() {
    return this.productOptions.filter((option) =>
      this.variants.find((variant) => variant.options.includes(option))
    )
  }

  constructor() {
    super()

    this.productCardType = ''
    this.activeVariant = null
    this.productOptions = []
    this.variants = []
    this.swatches = []
  }

  findVariantByOption(option) {
    return this.variants.find((variant) => variant.options.includes(option))
  }

  setActiveVariant(variant) {
    this.activeVariant = variant
  }

  handleOptionClick(evt) {
    const { option, variantId } = evt.target.dataset

    this.activeOption = option
    const activeVariant = this.variants.find(
      (variant) => variant.id == variantId
    )

    this.setActiveVariant(activeVariant)

    this.dispatchEvent(
      new CustomEvent('nova:option-selected', {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.activeVariant,
        },
      })
    )
  }

  render() {
    if (this.variants.length <= 1) return html``
    return html`<sl-carousel
      style="--aspect-ratio: auto;"
      mouse-dragging
      class="quick-shop__option_selector${this.productCardType === 'mini'
        ? ' quick-shop__option_selector-mini'
        : ''}"
    >
      ${this.optionsToShow.map((option, i) => {
        const variant = this.findVariantByOption(option)
        const stateClasses = {
          'quick-shop__option--sold-out': !variant?.available,
          'quick-shop__option--selected': variant.id === this.activeVariant?.id,
        }

        return html`
          <sl-carousel-item>
            <sl-button
              class="quick-shop__option ${classMap(stateClasses)}"
              data-variant-id="${variant.id}"
              data-option="${option}"
              aria-label="option: ${option}"
              variant="text"
              size="small"
              ?disabled="${variant.available === false}"
              @click="${this.handleOptionClick}"
            >
              ${option}
            </sl-button>
          </sl-carousel-item>
        `
      })}
    </sl-carousel>`
  }

  static styles = [
    css`
      .quick-shop__option_selector {
        width: 100%;
        display: flex;
        flex-wrap: nowrap;
        justify-content: flex-start;
        align-items: center;
        gap: 6px;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: none;
        scrollbar-width: none;
      }

      .quick-shop__option_selector::-webkit-scrollbar {
        display: none;
      }

      .quick-shop__option_selector::part(base) {
        gap: 0;
      }

      .quick-shop__option_selector::part(scroll-container) {
        --slide-size: 55px;
        --slide-gap: 10px;
      }

      .quick-shop__option {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 2px;
        border: 1px solid var(--nova-utility-grey-1);
        background: #fff;
        padding: 6px 4px;
      }

      .quick-shop__option:first-of-type {
        margin-left: var(--nova-space-xs);
      }

      .quick-shop__option:last-of-type {
        margin-right: var(--nova-space-xs);
      }

      .quick-shop__option[disabled] {
        cursor: not-allowed;
      }

      .quick-shop__option:not(.quick-shop__option--sold-out):hover {
        background-color: var(--nova-brand-primary);
        border: 1px solid var(--nova-brand-primary);
      }

      .quick-shop__option:not(
          .quick-shop__option--sold-out
        ):hover.quick-shop__option::part(label) {
        color: var(--nova-brand-secondary);
      }

      .quick-shop__option::part(base) {
        border-color: transparent;
        width: fit-content;
        min-height: auto;
        padding: 0;
        border-radius: 0;
        min-width: 45px;
      }

      .quick-shop__option::part(label) {
        color: #000;

        font-size: 14px;
        font-weight: 500;
        line-height: 120%; /* 16.8px */
        padding: 0;
      }

      .quick-shop__option--selected::part(label) {
        color: var(--nova-brand-ink);
      }

      .quick-shop__option--sold-out {
        background: var(--nova-utility-grey-2);
        border: 1px solid var(--nova-utility-grey-2);
      }

      .quick-shop__option--sold-out::part(base) {
        opacity: 1;
      }

      .quick-shop__option--sold-out::part(label) {
        color: #4b4b4b;
      }

      .quick-shop__option--sold-out::after {
        content: '';
        position: absolute;
        width: 100%; /* Adjusted to 100% to span the full width */
        height: 100%; /* Adjusted to 100% to span the full height */
        top: 0;
        left: 0;
        background: linear-gradient(
          to bottom left,
          transparent 48%,
          var(--nova-brand-secondary) 48%,
          var(--nova-brand-secondary) 52%,
          transparent 52%
        ); /* Using a linear gradient to create the diagonal line */
        transform: rotate(0deg); /* Reset rotation to default */
      }

      @media screen and (min-width: 990px) {
        .quick-shop__option_selector {
          flex-wrap: wrap;
        }

        .quick-shop__option_selector-mini.quick-shop__option_selector {
          flex-wrap: nowrap;
        }

        .quick-shop__option:first-of-type {
          margin-left: 0;
        }

        .quick-shop__option:last-of-type {
          margin-right: 0;
        }
      }
    `,
  ]
}

customElements.define('nova-product-card-quick-shop', NovaProductCardQuickShop)
